import { useCallback, useContext, useMemo } from 'react';
import { GlobalContext } from '../../library/Core/GlobalContext';
import { DomainElement } from '../../library/Core/SmartTypes';

/**
 * Custom hook for managing institute group and institute filtering
 * Used by SUPER_ADMIN users to filter by group and then by institute
 * 
 * Returns:
 * - groups: Array of all available school groups (non-group schools if no group filter selected)
 * - institutes: Array of institutes filtered by selected group
 * - getInstitutesByGroup: Function to get institutes for a specific group
 */
export const useGroupInstituteFilter = () => {
    const { globalState } = useContext(GlobalContext);

    // Get all school groups directly from SCHOOL_GROUP domain.
    // Exclude the placeholder 'NA' code (and any blank codes) — selecting it
    // sends 'NA' to the backend, which fails on the bigint group_id column
    // ("invalid input syntax for type bigint").
    const groups = useMemo(() => {
        const schoolGroups = globalState.domain.get('SCHOOL_GROUP') || [];

        return schoolGroups
            .filter((item: DomainElement) => {
                const code = String(item.code || '').toUpperCase();
                return code !== 'NA' && code !== '';
            })
            .map((item: DomainElement) => ({
                label: item.value,
                value: item.code,
            }));
    }, [globalState.domain]);

    // Set of valid SCHOOL_GROUP codes — anything outside this set counts as "non-group"
    const schoolGroupCodes = useMemo(() => {
        const schoolGroups = globalState.domain.get('SCHOOL_GROUP') || [];
        return new Set(schoolGroups.map((g: DomainElement) => g.code));
    }, [globalState.domain]);

    // Get institutes for a specific group or all non-group schools if no group selected
    const getInstitutesByGroup = useCallback((groupId?: string) => {
        const allInstitutes = globalState.domain.get('INSTITUTE_DOMAIN') || [];

        const isPublished = (item: DomainElement) => {
            const flexCol = item.flexCol ? JSON.parse(item.flexCol || '{}') : {};
            // Match both string '1' and number 1 — different domain feeds use different types
            return flexCol.is_published === '1' || flexCol.is_published === 1;
        };

        if (!groupId) {
            // Non-group schools: parentCode does not match any known SCHOOL_GROUP code.
            // This catches null, empty string, "0", legacy markers, etc. — anything that
            // isn't an actual school group.
            return allInstitutes
                .filter((item: DomainElement) => {
                    const inGroup = item.parentCode && schoolGroupCodes.has(item.parentCode);
                    return !inGroup && isPublished(item);
                })
                .map((item: DomainElement) => ({
                    label: item.value,
                    value: item.code,
                }));
        }

        // Filter institutes by the selected group (using parentCode to match the group)
        return allInstitutes
            .filter((item: DomainElement) => item.parentCode === groupId && isPublished(item))
            .map((item: DomainElement) => ({
                label: item.value,
                value: item.code,
            }));
    }, [globalState.domain, schoolGroupCodes]);

    return {
        groups,
        getInstitutesByGroup,
    };
};
